import React, {useCallback} from 'react';
import {useAppSelector, useAppDispatch} from '../../hooks/redux';
import {useAuth} from '../../hooks/useAuth';
import {AdminService} from '../../services/adminService';
import {setCurrentPatient} from '../../slices/patientSlice';
import {hideBanner} from '../../slices/bannerSlice';

const Banner: React.FC = () => {
    const {user, setUser} = useAuth();
    const dispatch = useAppDispatch();
    const {visible, message} = useAppSelector((state) => state.banner);

    // Check if user is being impersonated (patient switching) or admin is impersonating (admin feature)
    const originalAdminDataString = localStorage.getItem('originalAdminData');
    const originalAdminData = originalAdminDataString ? JSON.parse(originalAdminDataString) : null;
    const isPatientSwitching = originalAdminData?.switchUser === true;
    const isAdminImpersonating = !!originalAdminDataString && !isPatientSwitching;
    const isImpersonating = isPatientSwitching || isAdminImpersonating;

    const handleExitImpersonation = useCallback(async () => {
        try {
            // Get original admin data from localStorage
            const originalAdminData = localStorage.getItem('originalAdminData');
            if (!originalAdminData) {
                // If we don't have original admin data, just logout
                setUser(undefined as any);
                window.location.href = '/login';
                return;
            }

            const parsedAdminData = JSON.parse(originalAdminData);

            if (!parsedAdminData.userInfo || !parsedAdminData.token) {
                // Clean up bad data and logout
                localStorage.removeItem('originalAdminData');
                setUser(undefined as any);
                window.location.href = '/login';
                return;
            }

            try {
                // Try to call exit impersonation API with original admin token
                const exitResult = await AdminService.exitImpersonation(parsedAdminData.token);

                // Use the result from the API
                const returnUser = {
                    token: exitResult.result.token,
                    userInfo: {
                        ...exitResult.result.userInfo,
                        // Clear impersonation flags
                        impersonate_id: undefined,
                        isImpersonating: false,
                    },
                    username: exitResult.result.userInfo.email,
                    isLoggedIn: true,
                };

                setUser(returnUser);

                // Update Redux current patient back to admin
                const adminPatientData = {
                    id: exitResult.result.userInfo.id,
                    firstName: exitResult.result.userInfo.firstName,
                    lastName: exitResult.result.userInfo.lastName,
                    fullName:
                        exitResult.result.userInfo.fullName ||
                        `${exitResult.result.userInfo.firstName} ${exitResult.result.userInfo.lastName}`,
                    birthdate: exitResult.result.userInfo.birthday,
                };
                dispatch(setCurrentPatient(adminPatientData));
            } catch {
                // Fallback: restore from localStorage if API fails
                const returnUser = {
                    token: parsedAdminData.token,
                    userInfo: {
                        ...parsedAdminData.userInfo,
                        // Clear impersonation flags
                        impersonate_id: undefined,
                        isImpersonating: false,
                    },
                    username: parsedAdminData.username,
                    isLoggedIn: true,
                };

                setUser(returnUser);

                // Update Redux current patient back to admin
                const adminPatientData = {
                    id: parsedAdminData.userInfo.id,
                    firstName: parsedAdminData.userInfo.firstName,
                    lastName: parsedAdminData.userInfo.lastName,
                    fullName:
                        parsedAdminData.userInfo.fullName ||
                        `${parsedAdminData.userInfo.firstName} ${parsedAdminData.userInfo.lastName}`,
                    birthdate: parsedAdminData.userInfo.birthday,
                };
                dispatch(setCurrentPatient(adminPatientData));
            }

            // Hide the banner
            dispatch(hideBanner());

            // Clean up stored admin data
            localStorage.removeItem('originalAdminData');

            // Navigate back to dashboard
            window.location.href = '/';
        } catch {
            alert('Failed to exit impersonation. Please try again.');
        }
    }, [dispatch, setUser]);

    // Show banner if Redux banner is visible OR if user is being impersonated
    const shouldShowBanner = visible || isImpersonating;

    if (!shouldShowBanner) return null;

    return (
        <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-blue-800 text-sm">
            <div className="flex items-center justify-center">
                {isImpersonating ? (
                    <p>
                        {isPatientSwitching ? (
                            <>
                                Viewing user{' '}
                                {user?.userInfo?.fullName ||
                                    `${user?.userInfo?.firstName} ${user?.userInfo?.lastName}` ||
                                    'Unknown User'}{' '}
                                <button
                                    onClick={handleExitImpersonation}
                                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none font-medium">
                                    Switch Back
                                </button>
                            </>
                        ) : (
                            <>
                                Admin impersonating user{' '}
                                {user?.userInfo?.fullName ||
                                    `${user?.userInfo?.firstName} ${user?.userInfo?.lastName}` ||
                                    'Unknown User'}{' '}
                                <button
                                    onClick={handleExitImpersonation}
                                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none font-medium">
                                    Exit Impersonation
                                </button>
                            </>
                        )}
                    </p>
                ) : (
                    <p>{message}</p>
                )}
            </div>
        </div>
    );
};

export default Banner;
